/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import { Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextField } from '@fluentui/react/lib/TextField';
import { Toggle } from '@fluentui/react/lib/Toggle';
import * as React from 'react';
import { useState } from 'react';
import { currentModeVar } from '../../../apollo/reactiveVariables/TemplateInstanceVariables';
import { Constants } from '../../../common/Constants';
import { FormEntry, FormFillPreviewAdapter } from '../../../forms';
import { StepName } from '../../../logger/interfaces';
import { StatusName } from '../../../logger/ScenarioLogger';
import { getApprovalFileService } from '../../../service/file-upload/ApprovalsFileService';
import { ApprovalType } from '../../../service/gen';
import { WorkflowSchema } from '../../../service/gen/templates';
import { getAppHost } from '../../../utilities/TeamsUtils';
import {
    FormsRuntimeIframeEventHandlers,
    getFormsRuntimeIframeUrl,
    getTemplateScenarioName,
    useApprovalApproversInputField,
    useApprovalTitleInputField,
    useFilesInputField,
    useFormsRuntimeIframe,
} from '../../../utilities/TemplateUtils';
import { stringFormat } from '../../../utilities/Utils';
import { ApproverPeoplePicker, FileAttachments, TitleTextField } from '../../components';
import { ErrorPage } from '../ErrorPage';
import { LoadingPage } from '../LoadingPage';
import {
    getFileAttachmentStyles,
    getFormsRuntimeIframeStyles,
    getFormsRuntimeIframeWrapperStyles,
    getLabelStyles,
    getStackItemStyles,
} from './CreateTemplateApproval.styles';
import { TemplateStepApproversPicker } from './TemplatedStepApprovers.component';

export interface TemplateApprovalPreviewComponentProps {
    formId: string;
    formEntries?: FormEntry[];
    workflowSchema?: WorkflowSchema;
    scrollRef?: React.MutableRefObject<HTMLDivElement>;
    showFileUpload?: boolean;
}

export const TemplatedApprovalPreview: React.FC<TemplateApprovalPreviewComponentProps> = (
    props: TemplateApprovalPreviewComponentProps
): JSX.Element => {
    const { formId, formEntries, workflowSchema, showFileUpload } = props;
    const startTime = React.useRef<number>(performance.now());
    const currentMode = useReactiveVar(currentModeVar);

    const host = getAppHost();
    const logger = host.getLogger();
    const loc = host.getLocale();
    const formsRuntimeIframeUrl = getFormsRuntimeIframeUrl(formId, true);
    const { title, titleError, onTitleChange, validateTitle } = useApprovalTitleInputField();
    const textRef = React.useRef<ITextField>(null);
    React.useEffect((): void => {
        textRef.current?.focus();
    }, []);
    const {
        approvers,
        approversError,
        onApproversChange,
        validateApprovers,
        approversPickerDisabled,
        tooltip,
        allMustRespond,
        processing,
        setAllMustRespond,
        isSequential,
        stepApprovers,
        onStepApproversChange,
    } = useApprovalApproversInputField(workflowSchema, true);
    const fileService = getApprovalFileService();
    const fileUploadAcceptedTypes = Constants.FILE_UPLOAD_ACCEPTED_TYPES;
    const { fileAttachments, setFileAttachments } = useFilesInputField();

    const [loadingPageProps, setLoadingPageProps] = useState<{ message?: string }>({});
    const [errorPageProps, setErrorPageProps] = useState<{ message?: string; isRetriable?: boolean }>();

    const currentScenarioName = getTemplateScenarioName(currentMode);
    const onError = (_error: ErrorEvent): void => {
        // When preview we will not support retry for now.
        // TODO(wahan) Enhanced error message waiting for PM input.
        setErrorPageProps({});
        logger.markScenario(currentScenarioName, StepName.FormsRuntimeLoadFailed, StatusName.NA, {
            stepElapsed: performance.now() - startTime.current,
            data: { formId },
        });
    };
    const formsIFrameEventHandler: FormsRuntimeIframeEventHandlers = {
        formReadyEventHandler: (): void => {
            logger.markScenario(currentScenarioName, StepName.FormsRuntimeReady, StatusName.NA, {
                stepElapsed: performance.now() - startTime.current,
                data: { formId },
            });
            setLoadingPageProps(undefined);
        },
        authErrorHandler: onError,
        generalErrorHandler: onError,
        serverErrorHandler: onError,
    };
    const [formsIframeRef, , scrollRef, iframeHeight] = useFormsRuntimeIframe(formsIFrameEventHandler, props.scrollRef);

    const errorOrLoading = !!loadingPageProps || !!errorPageProps || processing;
    /* eslint-disable @microsoft/sdl/react-iframe-missing-sandbox */
    return (
        <div
            style={{
                position: 'relative',
                width: '100%',
                height: errorOrLoading ? 'calc(100% - 5px)' : 'auto',
                overflowX: 'hidden',
                overflowY: errorOrLoading ? 'hidden' : 'visible',
            }}>
            {errorOrLoading && (
                <div style={{ position: 'absolute', height: '100%', width: '100%' }}>
                    {errorPageProps ? <ErrorPage {...errorPageProps} /> : <LoadingPage {...loadingPageProps} />}
                </div>
            )}
            <div
                ref={scrollRef}
                style={{
                    visibility: errorOrLoading ? 'hidden' : 'visible',
                }}>
                <Stack>
                    <Stack.Item styles={getStackItemStyles()}>
                        <TitleTextField
                            componentRef={textRef}
                            label={loc.strings.request_title_text}
                            onValueChange={onTitleChange}
                            placeholder={loc.strings.request_title_placeholder_text}
                            errorMessage={titleError}
                            defaultValue={title}
                            maxLength={Constants.maxTitleCharacterLimit}
                            ariaLabel={loc.strings.request_title_text}
                            required
                        />
                    </Stack.Item>

                    <Stack.Item styles={getStackItemStyles()}>
                        {isSequential ? (
                            <TemplateStepApproversPicker
                                items={stepApprovers}
                                onApproversChange={onStepApproversChange}
                            />
                        ) : (
                            <ApproverPeoplePicker
                                tooltip={tooltip}
                                onApproversSelected={onApproversChange}
                                error={approversError}
                                currentSelectedItems={approvers}
                                disabled={approversPickerDisabled}
                                onBlur={(): void => {
                                    validateTitle();
                                    validateApprovers();
                                }}
                                required
                            />
                        )}
                    </Stack.Item>
                    {!isSequential && (
                        <Stack.Item>
                            {approversPickerDisabled ? (
                                <Label styles={getLabelStyles} className='template-preview-all-must-respond-label'>
                                    {allMustRespond
                                        ? loc.strings.all_must_respond_toggle_label
                                        : loc.strings.one_must_respond_label}
                                </Label>
                            ) : (
                                <Toggle
                                    label={loc.strings.all_must_respond_toggle_label}
                                    inlineLabel
                                    checked={allMustRespond}
                                    onChanged={setAllMustRespond}
                                    disabled={approvers?.length <= 1}
                                    aria-disabled={approvers?.length <= 1}
                                    ariaLabel={stringFormat(loc.strings.all_must_approve_aria_label, {
                                        state: allMustRespond
                                            ? loc.strings.checked_aria_label
                                            : loc.strings.unchecked_aria_label,
                                    })}
                                />
                            )}
                        </Stack.Item>
                    )}

                    {formId ? (
                        <Stack.Item styles={getFormsRuntimeIframeWrapperStyles(iframeHeight.height)}>
                            <iframe
                                title={loc.strings.forms_iframe_title}
                                ref={formsIframeRef}
                                src={formsRuntimeIframeUrl}
                                style={getFormsRuntimeIframeStyles()}
                            />
                        </Stack.Item>
                    ) : (
                        <FormFillPreviewAdapter
                            initialFormEntries={formEntries ?? []}
                            onFormReady={() => setLoadingPageProps(undefined)}
                        />
                    )}

                    {showFileUpload && (
                        <Stack.Item styles={getFileAttachmentStyles()}>
                            <FileAttachments
                                fileService={fileService}
                                showLabel
                                accept={fileUploadAcceptedTypes}
                                files={fileAttachments}
                                onFilesUpdate={setFileAttachments}
                                approvalType={ApprovalType.Basic.toString()}
                                required
                            />
                        </Stack.Item>
                    )}
                </Stack>
            </div>
        </div>
    );
    /* eslint-enable @microsoft/sdl/react-iframe-missing-sandbox */
};
